import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientService from '../services/clientService';
import moncashFeeService from '../services/moncashFeeService';

const toOption = (c) => ({
  value: c.id,
  label: `${c.firstName || ''} ${c.lastName || ''}`.trim() + (c.nifCin ? ` — ${c.nifCin}` : ''),
  data: c,
});

const TYPES = [
  { value: 'DEPOSIT', label: 'Dépôt' },
  { value: 'WITHDRAWAL', label: 'Retrait' },
];

const EMPTY_VALUES = {
  type: 'DEPOSIT',
  phone: '',
  amountInputHTG: '',
  feeIncluded: false,

  client: null,
  clientName: '',
  note: '',
};

function toIntSafe(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

function findFeeHTG(rules, amount) {
  if (!Array.isArray(rules) || rules.length === 0) return null;
  const a = Number(amount);
  if (!Number.isFinite(a)) return null;

  const sorted = [...rules].sort((x, y) => Number(x.minAmount) - Number(y.minAmount));
  const match = sorted.find((r) => {
    const min = Number(r.minAmount);
    const max = (r.maxAmount === null || r.maxAmount === undefined) ? null : Number(r.maxAmount);
    if (!Number.isFinite(min)) return false;
    if (max === null) return a >= min;
    return a >= min && a <= max;
  });

  if (!match) return null;
  const f = Number(match.feeHTG);
  return Number.isFinite(f) ? Math.trunc(f) : null;
}

function computePreview({ type, amountInputHTG, feeIncluded, feeHTG }) {
  const a = Math.trunc(Number(amountInputHTG || 0));
  const f = Math.trunc(Number(feeHTG || 0));

  if (a <= 0) return { amountAccountHTG: 0, amountCashHTG: 0, error: null };

  if (type === 'DEPOSIT') {
    if (feeIncluded) {
      return { amountAccountHTG: a, amountCashHTG: a + f, error: null };
    }
    const credit = a - f;
    if (credit < 0) return { amountAccountHTG: 0, amountCashHTG: 0, error: 'Frais > montant (dépôt avec frais retenus).' };
    return { amountAccountHTG: credit, amountCashHTG: a, error: null };
  }

  if (feeIncluded) {
    return { amountAccountHTG: a + f, amountCashHTG: a, error: null };
  }
  const cash = a - f;
  if (cash < 0) return { amountAccountHTG: 0, amountCashHTG: 0, error: 'Frais > montant (retrait avec frais retenus).' };
  return { amountAccountHTG: a, amountCashHTG: cash, error: null };
}

export default function MoncashModal({
  show,
  onHide,
  onSave,
  initialData = null,
}) {
  const init = useMemo(() => initialData ?? null, [initialData]);
  const isEdit = !!init?.id;

  const [selectKey, setSelectKey] = useState(0);

  // fee rules
  const [feeRules, setFeeRules] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: EMPTY_VALUES,
    shouldUnregister: true,
  });

  const selectedClient = watch('client');
  const type = watch('type');
  const amountInputHTG = watch('amountInputHTG');
  const feeIncluded = watch('feeIncluded');

  const amountInt = toIntSafe(amountInputHTG) ?? 0;

  const feeHTG = useMemo(() => {
    const f = findFeeHTG(feeRules, amountInt);
    return f ?? 0;
  }, [feeRules, amountInt]);

  const preview = useMemo(() => {
    return computePreview({ type, amountInputHTG: amountInt, feeIncluded: !!feeIncluded, feeHTG });
  }, [type, amountInt, feeIncluded, feeHTG]);

  const loadFees = useCallback(async (kind) => {
    setFeeError('');
    setFeeLoading(true);
    try {
      const res = await moncashFeeService.list(kind);
      setFeeRules(res?.rules || []);
    } catch (e) {
      setFeeRules([]);
      setFeeError(e?.response?.data?.message || 'Erreur chargement barème MonCash.');
    } finally {
      setFeeLoading(false);
    }
  }, []);

  const applyResetFromInit = useCallback(() => {
    reset({
      type: init?.type || 'DEPOSIT',
      phone: init?.phone || '',
      amountInputHTG: init?.amountInputHTG ?? '',
      feeIncluded: !!init?.feeIncluded,

      client: init?.client ? toOption(init.client) : null,
      clientName: init?.clientName || '',
      note: init?.note || '',
    });
  }, [init, reset]);

  useEffect(() => {
    if (!show) return;
    applyResetFromInit();
    setSelectKey((k) => k + 1);
  }, [show, applyResetFromInit]);

  useEffect(() => {
    if (!show) return;
    loadFees(type || 'DEPOSIT');
  }, [show, type, loadFees]);

  const handleClose = () => {
    reset(EMPTY_VALUES);
    setSelectKey((k) => k + 1);
    setFeeError('');
    setFeeRules([]);
    setFeeLoading(false);
    onHide();
  };

  // auto-fill clientName if client selected
  useEffect(() => {
    if (!show) return;
    const c = selectedClient?.data;
    if (!c) return;

    const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    if (!fullName) return;

    const current = String(getValues('clientName') || '').trim();
    if (current !== fullName) {
      setValue('clientName', fullName, { shouldValidate: true });
    }
  }, [show, selectedClient?.value, selectedClient?.data, setValue, getValues]);

  const loadClientOptions = async (inputValue, loadedOptions, { page }) => {
    const res = await clientService.searchPaged({ q: inputValue || '', page, pageSize: 20 });
    const rows = res?.rows || [];
    const options = rows.map(toOption);

    return {
      options,
      hasMore: page < (res?.totalPages || 1),
      additional: { page: page + 1 },
    };
  };

  const submit = (vals) => {
    const clientId = vals.client?.value ?? null;

    const payload = {
      type: String(vals.type || '').toUpperCase(),
      phone: String(vals.phone || '').trim(),
      amountInputHTG: toIntSafe(vals.amountInputHTG),
      feeIncluded: !!vals.feeIncluded,
      clientId,
      clientName: clientId ? null : (String(vals.clientName || '').trim() || null),
      note: String(vals.note || '').trim() || null,
    };

    onSave(payload);
  };

  const clientNameDisabled = !!selectedClient?.value;

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Modifier MonCash' : 'Nouvelle transaction MonCash'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(submit)}>
        <Modal.Body>
          {feeError && <Alert variant="danger">{feeError}</Alert>}

          {/* ✅ Bloc haut: type/phone/montant/frais inclus */}
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Type *</Form.Label>
                <Form.Select {...register('type', { required: 'Requis' })}>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Form.Select>
                {errors.type && <Form.Text className="text-danger">{errors.type.message}</Form.Text>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Téléphone *</Form.Label>
                <Form.Control
                  placeholder="Ex: +509..."
                  {...register('phone', { required: 'Requis' })}
                />
                {errors.phone && <Form.Text className="text-danger">{errors.phone.message}</Form.Text>}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Montant saisi (HTG) *</Form.Label>
                <Form.Control
                  type="number"
                  step="1"
                  {...register('amountInputHTG', {
                    required: 'Requis',
                    validate: (v) => {
                      const n = toIntSafe(v);
                      if (n === null || n <= 0) return 'Doit être > 0';
                      return true;
                    },
                  })}
                />
                {errors.amountInputHTG && (
                  <Form.Text className="text-danger">{errors.amountInputHTG.message}</Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6} className="d-flex align-items-end">
              <Form.Group className="w-100">
                <Form.Check
                  type="switch"
                  id="feeIncluded"
                  label="Frais payés séparément"
                  {...register('feeIncluded')}
                />
                <Form.Text className="text-muted">
                  Oui = le client paie le frais en plus. Non = le frais est retenu automatiquement dans le montant.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* ✅ AJUSTEMENT: Calcul automatique juste sous le montant */}
          <Alert variant="secondary" className="mt-3 mb-0">
            <div className="d-flex justify-content-between align-items-center">
              <div className="fw-semibold">Calcul automatique</div>
              {feeLoading && (
                <div className="d-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" />
                  <span className="small text-muted">Barème…</span>
                </div>
              )}
            </div>

            <div className="mt-2 small">
              <div className="d-flex justify-content-between">
                <span className="text-muted">Frais (HTG)</span>
                <span className="fw-semibold">{feeHTG}</span>
              </div>

              <div className="d-flex justify-content-between">
                <span className="text-muted">
                  {type === 'DEPOSIT' ? 'Crédit MonCash (HTG)' : 'Débit MonCash (HTG)'}
                </span>
                <span className="fw-semibold">{preview.amountAccountHTG}</span>
              </div>

              <div className="d-flex justify-content-between">
                <span className="text-muted">
                  {type === 'DEPOSIT' ? 'Cash à encaisser (HTG)' : 'Cash à décaisser (HTG)'}
                </span>
                <span className="fw-semibold">{preview.amountCashHTG}</span>
              </div>

              {preview.error && (
                <Alert variant="warning" className="mt-2 mb-0">
                  {preview.error}
                </Alert>
              )}
            </div>
          </Alert>

          <hr className="my-4" />

          {/* Client */}
          <Form.Group>
            <Form.Label>Client (optionnel)</Form.Label>
            <Controller
              control={control}
              name="client"
              render={({ field }) => (
                <AsyncPaginate
                  key={selectKey}
                  value={field.value}
                  onChange={field.onChange}
                  loadOptions={loadClientOptions}
                  additional={{ page: 1 }}
                  debounceTimeout={300}
                  placeholder="Sélectionner un client (facultatif)…"
                  isClearable
                />
              )}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Nom client / bénéficiaire {clientNameDisabled ? '(auto)' : '*'} </Form.Label>
            <Form.Control
              disabled={clientNameDisabled}
              placeholder="Nom si non-client"
              {...register('clientName', {
                validate: (v) => {
                  if (selectedClient?.value) return true;
                  return String(v || '').trim() ? true : 'Requis si pas de client';
                },
              })}
            />
            {errors.clientName && <Form.Text className="text-danger">{errors.clientName.message}</Form.Text>}
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Note (optionnel)</Form.Label>
            <Form.Control as="textarea" rows={2} {...register('note')} />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>Annuler</Button>
          <Button type="submit" disabled={!!preview.error}>
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}